import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useGenerateEntityPdf } from '@/features/vfo/hooks/useGenerateEntityPdf';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { ApiDocumentResponseV2 } from '@/shared/api';
import { CollectionName } from '@/shared/lib/sj-orm';
import { Button } from '@/shared/uikitV2/Button';
import { TabLabel, Tabs } from '@/shared/uikitV2/Tabs';
import { files } from '@/shared/utils/files';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { VfoImportDocumentModalProps } from '@/widgets/modals/types';

import {
  ConflictResult,
  DocumentsConflictResolutionMap,
} from '../VfoDocumentsConflictsModal/types';
import { VfoModalContainerV4 } from '../VfoModalContainerV4';

import { useAssetsTableOptions } from './hooks/useAssetsTableOptions';
import { usePersonalIdsTableOptions } from './hooks/usePersonalIdsTableOptions';
import { usePrivateDocumentsTableOptions } from './hooks/usePrivateDocumentsTableOptions';
import { EmptyList } from './EmptyList';
import { EntitiesTable } from './EntitiesTable';
import { isCopyTitle, isSelectionValid, makeCopyTitle } from './helpers';
import { DocumentsMap } from './types';

export const VfoImportDocumentModal: FC<VfoImportDocumentModalProps> = ({
  defaultCaseId,
  onFinish,
}) => {
  const { t: tc } = useTranslation('common');
  const { t: tn } = useTranslation('shared/collections', { keyPrefix: 'names' });
  const { t: tw } = useTranslation('widgets/modals', { keyPrefix: 'vfoImportDocument' });
  const modals = useModalsStore();
  const { generateEntity } = useGenerateEntityPdf();
  const { getCaseMemberById } = useCases();
  const { addDocument, documents, deleteDocument } = useDocuments();
  const { currentUser } = useMembers();

  const assetsOptions = useAssetsTableOptions();
  const privateDocumentsOptions = usePrivateDocumentsTableOptions();
  const personalIdsOptions = usePersonalIdsTableOptions();

  const optionsList = [assetsOptions, privateDocumentsOptions, personalIdsOptions];

  const initialOptions = optionsList.find((item) => item.data.length);
  const initialTab = initialOptions?.collectionName ?? optionsList[0].collectionName;

  const [selected, setSelected] = useState<DocumentsMap>(new Map());
  const [tab, setTab] = useState<CollectionName>(initialTab);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tabs = useMemo(
    () => [
      {
        key: CollectionName.ASSETS,
        label: <TabLabel label={tn('assets')} count={assetsOptions.data.length} />,
      },
      {
        key: CollectionName.PRIVATE_DOCUMENTS,
        label: (
          <TabLabel label={tn('privateDocuments')} count={privateDocumentsOptions.data.length} />
        ),
      },
      {
        key: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
        label: (
          <TabLabel
            label={tn('personalIdentifiersDocuments')}
            count={personalIdsOptions.data.length}
          />
        ),
      },
    ],
    [
      assetsOptions.data.length,
      privateDocumentsOptions.data.length,
      personalIdsOptions.data.length,
    ],
  );

  const tablesByTab: Partial<Record<CollectionName, ReactNode>> = {
    [CollectionName.ASSETS]: (
      <EntitiesTable
        options={assetsOptions}
        selected={selected}
        setSelected={setSelected}
        showValidationErrors={showValidationErrors}
        defaultCaseId={defaultCaseId}
      />
    ),
    [CollectionName.PRIVATE_DOCUMENTS]: (
      <EntitiesTable
        options={privateDocumentsOptions}
        selected={selected}
        setSelected={setSelected}
        showValidationErrors={showValidationErrors}
        defaultCaseId={defaultCaseId}
      />
    ),
    [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: (
      <EntitiesTable
        options={personalIdsOptions}
        selected={selected}
        setSelected={setSelected}
        showValidationErrors={showValidationErrors}
        defaultCaseId={defaultCaseId}
      />
    ),
  };

  const processDocuments = async (conflicts?: DocumentsConflictResolutionMap) => {
    const entries = Array.from(selected.entries());

    const results = await Promise.allSettled(
      entries.map(async ([dtoId, caseId]) => {
        const existingDocumentResolution = conflicts?.get(dtoId);

        if (existingDocumentResolution === ConflictResult.SKIP) {
          return Promise.resolve();
        }

        if (existingDocumentResolution === ConflictResult.REPLACE) {
          const existingDocument = documents.find((doc) => doc.dtoId === dtoId);
          await deleteDocument(existingDocument?.id || '');
        }

        const data = await generateEntity(dtoId);
        if (!data || !caseId || !currentUser?.profileId) return Promise.reject();
        let title = data.title;

        if (existingDocumentResolution === ConflictResult.COPY) {
          const existingDocuments = documents.filter((doc) => doc.dtoId === dtoId);
          title = makeCopyTitle(data.title, existingDocuments.length);
        }

        const uploaderCaseMember = getCaseMemberById(caseId, currentUser?.profileId);
        if (!uploaderCaseMember?.profileId) return Promise.reject();

        const content = await files.toBase64(data.file);
        if (!content) return Promise.reject();

        return addDocument({
          title,
          fileContent: content,
          fileName: data.file.name,
          fileSizeBytes: data.file.size,
          fileType: data.file.type,
          uploaderProfileId: uploaderCaseMember.profileId,
          caseIds: [caseId],
          source: 'EXTERNAL',
          dtoId,
        });
      }),
    );

    const addedDocuments = results
      .filter((result): result is PromiseFulfilledResult<ApiDocumentResponseV2> => {
        return result.status === 'fulfilled' && Boolean(result.value);
      })
      .map((result) => result.value);

    return addedDocuments;
  };

  const handleAdd = async () => {
    setShowValidationErrors(true);
    if (!isSelectionValid(selected) || isSubmitting) return;
    setIsSubmitting(true);

    const conflicts = documents.filter(
      (doc) => doc.dtoId && selected.has(doc.dtoId) && !isCopyTitle(doc.title),
    );

    if (conflicts.length) {
      modals.closeModal();
      modals.openModal({
        type: 'VfoDocumentsConflictsModal',
        conflicts,
        onResolve: processDocuments,
      });
      setIsSubmitting(false);
      return;
    }

    const addedDocuments = await processDocuments();
    setIsSubmitting(false);
    modals.closeModal();
    onFinish?.(addedDocuments);
  };

  return (
    <VfoModalContainerV4 title={tw('title')}>
      <ModalBody>
        {initialOptions ? (
          <>
            <Tabs
              style={{ marginBottom: 8 }}
              items={tabs}
              defaultValue={tab}
              onChange={(value) => setTab(value as CollectionName)}
            />
            <TableContainer>{tablesByTab[tab]}</TableContainer>
          </>
        ) : (
          <EmptyList />
        )}
      </ModalBody>
      <Buttons>
        <Button size="middle" type="default" onClick={modals.closeModal}>
          {tc('cancel')}
        </Button>
        <Button
          size="middle"
          disabled={!initialOptions || !selected.size}
          loading={isSubmitting}
          onClick={handleAdd}
        >
          {tc('add')}
        </Button>
      </Buttons>
    </VfoModalContainerV4>
  );
};

const ModalBody = styled.div`
  padding: 24px;
  padding-bottom: 0;
`;

const TableContainer = styled.div`
  height: 340px;
  overflow: auto;
`;

const Buttons = styled.div`
  padding: 16px 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: end;
`;
